import { forwardRef, memo, useCallback, useState } from 'react';
import type { DateOrTimeView, DateTimePickerProps } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { FormHelperText, InputAdornment, InputLabel } from '@mui/material';
import { StyledDateTimePicker, Wrapper } from './DatePicker.styles';
import { cs, enUS, sk } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import type { FormItemUiDefinition } from '../../../types/formItem.ts';
import { getIcon } from '../../../utils/common.ts';
import { getUiDefinitionContainerStyles, getUIDefinitionIconStyles, getUiDefinitionTextStyles } from '../../../utils/styles.ts';

interface Props extends Omit<DateTimePickerProps<Date>, 'value'> {
  error?: boolean;
  lightError?: boolean;
  helperText?: string;
  label?: string;
  placeholder?: string;
  value: Date | null;
  format?: string;
  background?: 'transparent' | 'white';
  fullWidth?: boolean;
  formItemUiDefinition?: FormItemUiDefinition | null;
  dataTestId?: string;
}

export const DatePicker = memo(
  forwardRef<HTMLInputElement, Props>(
    ({ label, error, lightError, helperText, placeholder: _placeholder, format = 'dd.MM.yyyy', formItemUiDefinition, dataTestId, ...rest }, ref) => {
      const { t, i18n } = useTranslation();
      const [open, setOpen] = useState(false);

      const Icon = formItemUiDefinition?.icon ? getIcon(formItemUiDefinition?.icon) : null;

      const boxStyles = getUiDefinitionContainerStyles(formItemUiDefinition);
      const textStyles = getUiDefinitionTextStyles(formItemUiDefinition);

      const getLocale = useCallback(() => {
        const language = i18n.language;
        switch (language) {
          case 'en':
            return enUS;
          case 'sk':
            return sk;
          case 'cs':
            return cs;
          default:
            return cs;
        }
      }, [i18n.language]);

      const handleOpen = useCallback(() => {
        if (rest.disabled) return;
        setOpen(true);
      }, [rest.disabled]);

      const handleClose = useCallback(() => {
        setOpen(false);
      }, []);

      const hasTimeViews = useCallback((views?: readonly DateOrTimeView[] | undefined) => {
        if (!views) return true;
        return views.some((view) => ['hours', 'minutes', 'seconds'].includes(view));
      }, []);

      return (
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={getLocale()}
          localeText={{
            clearButtonLabel: t('common.clear'),
            todayButtonLabel: t('common.date.today'),
            okButtonLabel: t('common.confirm'),
            fieldDayPlaceholder: () => '',
            fieldMonthPlaceholder: () => '',
            fieldYearPlaceholder: () => '',
            fieldHoursPlaceholder: () => '',
            fieldMinutesPlaceholder: () => '',
            fieldSecondsPlaceholder: () => '',
            fieldMeridiemPlaceholder: () => '',
          }}>
          <Wrapper>
            <InputLabel>{label}</InputLabel>
            <StyledDateTimePicker
              ref={ref}
              data-testid={dataTestId}
              $error={error}
              lightError={lightError}
              {...rest}
              value={rest.value ? new Date(rest.value) : null}
              closeOnSelect={false}
              open={open}
              onOpen={handleOpen}
              onClose={handleClose}
              ampm={false}
              format={hasTimeViews(rest?.views) && format === 'dd.MM.yyyy' ? 'dd.MM.yyyy HH:mm' : format}
              slotProps={{
                textField: {
                  error,
                  placeholder: '',
                  inputProps: {
                    placeholder: '',
                  },
                  onClick: handleOpen,
                  InputProps: {
                    sx: { ...textStyles, ...boxStyles },
                    onClick: handleOpen,
                    startAdornment: Icon ? (
                      <InputAdornment position="start">
                        <Icon sx={getUIDefinitionIconStyles(formItemUiDefinition)} />
                      </InputAdornment>
                    ) : null,
                  },
                },
                popper: {
                  disablePortal: false,
                  sx: {
                    zIndex: 1000000,
                    '& .MuiPaper-root': {
                      boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1)',
                      borderRadius: '8px',
                    },
                  },
                },
                layout: {
                  sx: {
                    '& .MuiTabs-root.MuiPickersLayout-tabs.MuiDateTimePickerTabs-root': {
                      display: !hasTimeViews(rest?.views) ? 'none' : undefined,
                    },
                  },
                },
                actionBar: {
                  actions: ['clear', 'today', 'accept'],
                },
                dialog: {
                  disableEscapeKeyDown: false,
                },
              }}
            />

            <FormHelperText error={error} variant="filled">
              {helperText ?? ''}
            </FormHelperText>
          </Wrapper>
        </LocalizationProvider>
      );
    }
  )
);
